import type { LadoComparacao } from '@baiocchi/shared'
import { Alerta, CabecalhoCartao, Cartao, Esqueleto, EstadoVazio, Etiqueta } from '@baiocchi/ui'
import { useQueries } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { AvisoDefasagem, CartaoDeMetrica, PainelSaude, formatarMetrica } from './componentes'
import { FiltrosMetaAds, parametrosDoFiltro, rotularOperacao } from './filtros'

/**
 * TELA-META-05 — Métricas e comparação Venda × Aluguel (§5 e §28).
 *
 * ## Duas coisas na mesma tela, e o motivo
 *
 * A primeira metade é o detalhamento das métricas do recorte selecionado — os onze cartões
 * com comparação sobre o período anterior. A segunda é a comparação entre as duas operações,
 * que **ignora o filtro de conta** de propósito: comparar venda com aluguel só faz sentido
 * olhando as duas ao mesmo tempo, e um seletor de conta ativo tornaria a seção vazia sem
 * explicação.
 *
 * ## Por que a comparação é por CPL
 *
 * É a métrica que a operação usa para decidir orçamento. E por que ela pode dizer "sem
 * vencedor": quando a diferença é menor que 10%, está dentro da variação normal entre
 * semanas, e apontar um vencedor faria o gestor mover orçamento por ruído. Quando falta
 * lead em uma das operações, não há base — e "empate" sugeriria que a conta foi feita.
 */
export function PaginaMetaAdsMetricas() {
  const { filtros, definir } = useFiltrosUrl()
  const parametros = parametrosDoFiltro(filtros)

  const [contas, painel, comparacao] = useQueries({
    queries: [
      {
        queryKey: chaves.metaAds.contas,
        queryFn: ({ signal }: { signal: AbortSignal }) => metaAdsApi.contas(signal),
        staleTime: 300_000,
      },
      {
        queryKey: chaves.metaAds.painel(parametros),
        queryFn: ({ signal }: { signal: AbortSignal }) => metaAdsApi.painel(parametros, signal),
        staleTime: 60_000,
      },
      {
        // Sem `contaId`: a comparação é sempre entre todas as contas ativas, agrupadas por
        // operação. Ver o cabeçalho.
        queryKey: chaves.metaAds.comparacao({ periodo: parametros.periodo }),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          metaAdsApi.comparacaoOperacoes({ periodo: parametros.periodo }, signal),
        staleTime: 60_000,
      },
    ],
  })

  const dados = painel.data?.dados
  const comp = comparacao.data?.dados

  return (
    <>
      <CabecalhoPagina
        titulo="Métricas"
        descricao="Detalhamento do período e comparação entre as operações de venda e aluguel."
      />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      {painel.isError ? (
        <EstadoDeErro erro={painel.error} aoTentarNovamente={() => void painel.refetch()} />
      ) : null}

      {painel.isLoading ? <Esqueleto className="mb-4 h-48 w-full" /> : null}

      {dados !== undefined ? (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {dados.cartoes.map((cartao) => (
              <CartaoDeMetrica key={cartao.chave} cartao={cartao} />
            ))}
          </div>

          <div className="mb-6">
            <PainelSaude saude={dados.saude} />
          </div>
        </>
      ) : null}

      <h2 className="mb-3 text-lg font-semibold text-conteudo">Venda × Aluguel</h2>

      {comparacao.isLoading ? <Esqueleto className="h-48 w-full" /> : null}

      {comp !== undefined ? (
        comp.lados.length < 2 ? (
          <Cartao>
            <EstadoVazio
              titulo="Comparação indisponível"
              descricao="A comparação exige ao menos duas operações com contas ativas cadastradas — por exemplo uma conta de venda e uma de aluguel."
            />
          </Cartao>
        ) : (
          <>
            {comp.maisEficiente !== null ? (
              <Alerta tom="sucesso" className="mb-4">
                <span className="font-medium">
                  {rotularOperacao(comp.maisEficiente.operacao)} está mais eficiente.
                </span>{' '}
                {comp.maisEficiente.justificativa}
              </Alerta>
            ) : (
              <Alerta tom="info" className="mb-4">
                Sem vencedor claro no período: a diferença de custo por lead está dentro da
                variação normal entre semanas, ou falta volume de leads em uma das operações.
                Mover orçamento agora seria decidir por ruído.
              </Alerta>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {comp.lados.map((lado) => (
                <LadoOperacao key={lado.operacao} lado={lado} />
              ))}
            </div>
          </>
        )
      ) : null}

      <div className="mt-4">
        <AvisoDefasagem
          defasagemMinutos={painel.data?.meta.defasagemMinutos ?? 0}
          atualizadoEm={painel.data?.meta.atualizadoEm}
        />
      </div>
    </>
  )
}

function LadoOperacao({ lado }: { readonly lado: LadoComparacao }) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={
          <span className="flex flex-wrap items-center gap-2">
            {rotularOperacao(lado.operacao)}
            <Etiqueta tom={lado.saude.faixa === 'EXCELENTE' ? 'sucesso' : 'neutro'} tamanho="sm">
              Saúde {lado.saude.avaliavel ? `${lado.saude.nota}/100` : '—'}
            </Etiqueta>
          </span>
        }
        descricao={lado.contas.join(' · ')}
      />
      <div className="p-4 sm:p-5">
        <dl className="space-y-1.5 text-sm">
          {[
            { rotulo: 'Investimento', valor: formatarMetrica(lado.metricas.gasto, 'MOEDA') },
            { rotulo: 'Leads', valor: formatarMetrica(lado.metricas.leads, 'INTEIRO') },
            { rotulo: 'Custo por lead', valor: formatarMetrica(lado.metricas.cpl, 'MOEDA') },
            { rotulo: 'CTR', valor: formatarMetrica(lado.metricas.ctr, 'PERCENTUAL') },
            { rotulo: 'CPC', valor: formatarMetrica(lado.metricas.cpc, 'MOEDA') },
            {
              rotulo: 'Conversão de clique em lead',
              valor: formatarMetrica(lado.metricas.taxaConversao, 'PERCENTUAL'),
            },
          ].map((linha) => (
            <div key={linha.rotulo} className="flex justify-between gap-3">
              <dt className="text-conteudo-suave">{linha.rotulo}</dt>
              <dd className="tabular-nums font-medium text-conteudo">{linha.valor}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Cartao>
  )
}
